import React from "react";
import { useNavigate } from "react-router-dom";
import { InviteModal } from "../modal";
import { createInitialsAvatar } from "../../utils/initialsAvatar";

const resolveImage = (imagePath, name) => {
  if (!imagePath) {
    return createInitialsAvatar(name);
  }

  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  return imagePath;
};

const formatName = (staff) => {
  if (!staff) {
    return "-";
  }

  if (staff.fullName) {
    return staff.fullName;
  }

  return `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || "Unnamed staff";
};

const StaffListModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  staff = [],
  loading = false,
  error = "",
  onRefresh,
  primaryActionLabel,
  onPrimaryAction,
  primaryActionLoadingId = "",
  primaryActionDisabledPredicate,
}) => {
  const navigate = useNavigate();

  return (
    <InviteModal
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="my-4">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-2xl font-semibold text-black font-Outfit">{title}</p>
            {subtitle ? <p className="text-sm text-gray-600 mt-1">{subtitle}</p> : null}
          </div>
          {typeof onRefresh === "function" ? (
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="px-4 py-2 rounded-lg border border-[#1234CD] text-[#1234CD] disabled:opacity-60"
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          ) : null}
        </div>

        {error ? (
          <p className="mb-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}

        {loading ? (
          <p className="text-sm text-gray-600 py-4">Loading staff...</p>
        ) : staff.length === 0 ? (
          <p className="text-sm text-gray-600 py-4">No staff found.</p>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {staff.map((member) => {
              const actionDisabled =
                typeof primaryActionDisabledPredicate === "function"
                  ? primaryActionDisabledPredicate(member)
                  : false;

              return (
                <div
                  key={member.id}
                  className="rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={resolveImage(member.profilePhotoUrl, formatName(member))}
                      alt={formatName(member)}
                      className="w-14 h-14 rounded-full object-cover border border-gray-200"
                    />

                    <div>
                      <p className="font-semibold text-[#071C50]">{formatName(member)}</p>
                      <p className="text-sm text-gray-600">
                        {member.assignmentLabel || member.positionName || member.departmentName || "Staff"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {member.officialEmail || member.personalEmail || member.phone || "No contact info"}
                      </p>
                      <p className="text-xs text-gray-500">
                        Employee ID: {member.employeeNumber || member.id}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/employee/employeespage?id=${encodeURIComponent(member.id)}`)}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm"
                    >
                      View Profile
                    </button>

                    {typeof onPrimaryAction === "function" && primaryActionLabel ? (
                      <button
                        type="button"
                        onClick={() => onPrimaryAction(member.id)}
                        disabled={actionDisabled || primaryActionLoadingId === member.id}
                        className="px-3 py-2 rounded-lg text-sm bg-[#1234CD] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {primaryActionLoadingId === member.id
                          ? "Saving..."
                          : actionDisabled
                            ? "Current"
                            : primaryActionLabel}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </InviteModal>
  );
};

export default StaffListModal;
